import {animate, state, style, transition, trigger} from '@angular/animations';
import {AfterViewInit, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {MatDialog, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {ApiService} from '../../services/api.service';
import {ErrorHelper} from '../../common/ErrorHelper';
import {NotifierService} from 'angular-notifier';

export interface DynamicImagePack {
  disId: string;
  lastModifiedDate: Date;
  lastPreprodPromoteDate: Date;
  lastProdPromoteDate: Date;
}

@Component({
  selector: 'app-status-dynamic',
  templateUrl: './status-dynamic.component.html',
  styleUrls: ['./status-dynamic.component.css'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class StatusDynamicComponent implements OnInit, AfterViewInit {

  displayedColumns: string[] = ['lastModifiedDate', 'disId', 'lastPreprodPromoteDate', 'lastProdPromoteDate', 'action'];
  expandedElement: DynamicImagePack | null;
  dataSource: MatTableDataSource<DynamicImagePack>;
  filterValues = {
    filename: '',
    fromDate: '',
    toDate: ''
  };
  filenameFilter = new FormControl();
  filterForm = new FormGroup({
    fromDate: new FormControl(),
    toDate: new FormControl(),
  });

  get fromDate(): Date {
    return this.filterForm.get('fromDate').value;
  }

  get toDate(): Date {
    return this.filterForm.get('toDate').value;
  }

  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private apiService: ApiService,
    private notifierService: NotifierService,
    public dialog: MatDialog
  ) {
    this.dataSource = new MatTableDataSource<DynamicImagePack>();
  }

  ngOnInit(): void {
    this.loadDataAsync();
  }

  ngAfterViewInit(): void {
    this.filenameFilter.valueChanges
      .subscribe(name => {
        this.filterValues.filename = name;
        this.dataSource.filter = JSON.stringify(this.filterValues);
      });
    this.filterForm.controls.fromDate.valueChanges
      .subscribe(
        fromDate => {
          this.filterValues.fromDate = fromDate;
          this.dataSource.filter = JSON.stringify(this.filterValues);
        }
      );
    this.filterForm.controls.toDate.valueChanges
      .subscribe(
        toDate => {
          this.filterValues.toDate = toDate;
          this.dataSource.filter = JSON.stringify(this.filterValues);
        }
      );
  }

  async loadDataAsync(): Promise<any> {
    try {
      await this.loadData();
    } catch (error) {
      console.log(error);
    } finally {
      this.dataSource.sort = this.sort;
      this.dataSource.filterPredicate = (data: DynamicImagePack, filter) => {
        const searchTerms = JSON.parse(filter);
        const fileNameFilter = data.disId.toLowerCase().includes(searchTerms.filename.toLowerCase());
        const fromFilter = data.lastModifiedDate >= searchTerms.fromDate;
        const toFilter = data.lastModifiedDate <= searchTerms.toDate;
        if (this.fromDate && this.toDate) {
          return fromFilter && toFilter && fileNameFilter;
        } else if (this.fromDate) {
          return fromFilter && fileNameFilter;
        } else if (this.toDate) {
          return toFilter && fileNameFilter;
        }
        return fileNameFilter;
      };
    }
  }

  loadData(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      return this.apiService.getAllShortStatus().toPromise().then(
        (data) => {
          const dynamicImageSets = data;
          this.dataSource = new MatTableDataSource<DynamicImagePack>(dynamicImageSets);
          console.log('Fetched the dynamic image packs');
          resolve(true);
        },
        (error) => {
          const errorMessage = ErrorHelper.disectError('load status', error);
          this.notifierService.notify('error', errorMessage);
          resolve(false);
        });
    });
  }

  reset(): void {
    this.filenameFilter = new FormControl();
    this.filterForm = new FormGroup({
      fromDate: new FormControl(),
      toDate: new FormControl(),
    });

    this.filterValues.filename = '';
    this.filterValues.fromDate = '';
    this.filterValues.toDate = '';
    this.dataSource.filter = JSON.stringify(this.filterValues);
  }

}
